import { Badge, Box, Button, Flex, HStack, Input, SimpleGrid, Text, Tooltip, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { envTypeToColorScheme } from '@resistapp/client/components/shared/env-badge';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import {
  useAdminSampleAccess,
  useAdminSampleChips,
  useAdminSamples,
  useCountries,
  useProjects,
} from '@resistapp/client/hooks/api';
import { AdminSampleRow } from '@resistapp/common/api-types';
import { SampleType, friendlySampleType } from '@resistapp/common/sampling-point-types';
import { AccessLevel, SampleStatus } from '@resistapp/common/types';
import type { GroupBase } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParamsContext } from '../../contexts/search-params-context';

type Option = { value: string | number; label: string };

export function AdminSamplesView() {
  const { searchParams: routerSearchParams } = useSearchParamsContext();
  const { data: projects } = useProjects();
  const { data: countriesByAlpha3 } = useCountries();

  // Filters
  const [projectOpt, setProjectOpt] = useState<Option | null>(null);
  const [envNameFilter, setEnvNameFilter] = useState('');
  const [envNameInput, setEnvNameInput] = useState('');
  const [envTypes, setSampleTypes] = useState<Option[]>([]);
  const [statuses, setStatuses] = useState<Option[]>([]);
  const [countries, setCountries] = useState<Option[]>([]);
  const [chipIdFilter, setChipIdFilter] = useState('');
  const [chipIdInput, setChipIdInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<Option>({ value: 100, label: '100' });
  const [sampleIdFilter] = useState<string>(routerSearchParams.get('sampleId') || '');

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (projectOpt) p.set('projectId', String(projectOpt.value));
    if (envNameFilter.trim()) p.set('envName', envNameFilter.trim());
    if (envTypes.length) p.set('envType', envTypes.map(o => o.value).join(','));
    if (statuses.length) p.set('status', statuses.map(o => o.value).join(','));
    if (countries.length) p.set('country', countries.map(o => o.value).join(','));
    if (chipIdFilter.trim()) p.set('chipId', chipIdFilter.trim());
    if (sampleIdFilter.trim()) p.set('sampleId', sampleIdFilter.trim());
    p.set('page', String(page));
    p.set('pageSize', String(pageSize.value));
    return p;
  }, [projectOpt, envNameFilter, envTypes, statuses, countries, chipIdFilter, sampleIdFilter, page, pageSize]);

  const { data, isPending, error } = useAdminSamples(qs);

  const projectOptions = useMemo<Option[]>(
    () =>
      (projects || [])
        .slice()
        .sort((a, b) => {
          const tb = new Date(b.createdAt).getTime();
          const ta = new Date(a.createdAt).getTime();
          const bHas = Number.isFinite(tb);
          const aHas = Number.isFinite(ta);
          if (aHas && bHas) return tb - ta;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          return b.id - a.id;
        })
        .map(p => ({ value: p.id, label: p.name })),
    [projects],
  );
  const envTypeOptions = useMemo<Option[]>(
    () => Object.values(SampleType).map(t => ({ value: t, label: friendlySampleType(t) })),
    [],
  );
  const statusOptions = useMemo<Option[]>(
    () => Object.values(SampleStatus).map(s => ({ value: s, label: String(s).replace(/_/g, ' ') })),
    [],
  );
  const countryOptions = useMemo<Option[]>(() => {
    if (!countriesByAlpha3) return [];
    return Object.entries(countriesByAlpha3).map(([alpha3, c]) => ({ value: alpha3, label: c.name }));
  }, [countriesByAlpha3]);
  const pageSizeOptions: Option[] = useMemo(() => [20, 50, 100].map(n => ({ value: n, label: String(n) })), []);

  const statusColorScheme = (status: string | null | undefined): string => {
    switch (status) {
      case SampleStatus.APPROVED:
        return 'green';
      case SampleStatus.APPROVED_WITH_ISSUES:
      case SampleStatus.APPROVED_SKIP_RESULT_STATISTICS:
        return 'orange';
      case SampleStatus.UNAPPROVED:
        return 'gray';
      case SampleStatus.FAILED:
        return 'red';
      case SampleStatus.POOLED:
        return 'blue';
      case SampleStatus.DUPLICATE:
        return 'purple';
      default:
        return 'gray';
    }
  };

  // Removed statusLetter: we now display the full status text in the badge.

  const formatSampleNumBioRep = (num: number | null, bioRep: string | null): string => {
    const numStr = num == null ? '' : String(num);
    const repStr = bioRep == null ? '' : String(bioRep);
    const combined = `${numStr}${repStr}`;
    return combined.length ? combined : '-';
  };

  if (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
  if (isPending) {
    return <Loading />;
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Samples" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={theme.spacing[3]}>
          <ResistomapSelect<Option, false, GroupBase<Option>>
            options={projectOptions}
            value={projectOpt}
            onChange={opt => {
              setProjectOpt(opt);
            }}
            placeholder="Project"
            isClearable
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          />
          <Input
            placeholder="Sampling point name"
            value={envNameInput}
            onChange={e => {
              setEnvNameInput(e.target.value);
            }}
            onBlur={() => {
              const trimmed = envNameInput.trim();
              if (trimmed !== envNameFilter) {
                setEnvNameFilter(trimmed);
                setPage(1);
              }
              setEnvNameInput(trimmed);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const trimmed = envNameInput.trim();
                if (trimmed !== envNameFilter) {
                  setEnvNameFilter(trimmed);
                  setPage(1);
                }
                setEnvNameInput(trimmed);
              }
              if (event.key === 'Escape') {
                setEnvNameInput(envNameFilter);
              }
            }}
            bg="white"
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={envTypeOptions}
            value={envTypes}
            onChange={opts => {
              setSampleTypes(opts as Option[]);
            }}
            placeholder="Sample type"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
            formatOptionLabel={opt => (
              <Badge colorScheme={envTypeToColorScheme(opt.value as string)}>
                {friendlySampleType(opt.value as SampleType)}
              </Badge>
            )}
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={statusOptions}
            value={statuses}
            onChange={opts => {
              setStatuses(opts as Option[]);
            }}
            placeholder="Sample status"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
            formatOptionLabel={opt => (
              <Badge colorScheme={statusColorScheme(opt.value as string)}>{String(opt.value).replace(/_/g, ' ')}</Badge>
            )}
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={countryOptions}
            value={countries}
            onChange={opts => {
              setCountries(opts as Option[]);
            }}
            placeholder="Country"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          />
          <Input
            placeholder="Chip ID"
            value={chipIdInput}
            onChange={e => {
              setChipIdInput(e.target.value);
            }}
            onBlur={() => {
              const trimmed = chipIdInput.trim();
              if (trimmed !== chipIdFilter) {
                setChipIdFilter(trimmed);
                setPage(1);
              }
              setChipIdInput(trimmed);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const trimmed = chipIdInput.trim();
                if (trimmed !== chipIdFilter) {
                  setChipIdFilter(trimmed);
                  setPage(1);
                }
                setChipIdInput(trimmed);
              }
              if (event.key === 'Escape') {
                setChipIdInput(chipIdFilter);
              }
            }}
            bg="white"
          />
        </SimpleGrid>

        <Box>
          {data.rows.length === 0 ? (
            <Text>No samples match your filters.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {data.rows.map((row: AdminSampleRow) => {
                const s = row.status;
                return (
                  <Box key={row.sampleId} padding={2} borderRadius={6} bg="white" boxShadow="sm">
                    <Flex justify="space-between" align="center">
                      <Box>
                        <HStack spacing={3} align="baseline">
                          <HStack
                            as={Link}
                            to={`/admin/sample/${row.sampleId}`}
                            spacing={3}
                            align="baseline"
                            style={{ textDecoration: 'none' }}
                          >
                            <Text color="gray.600">{row.sampleId}</Text>
                            <Text fontWeight="bold">{`${formatSampleNumBioRep(row.number, row.bioRep)} - ${row.samplingPoint.name}`}</Text>
                          </HStack>
                          <Badge colorScheme={statusColorScheme(s)} title={s}>
                            {String(s).replace(/_/g, ' ')}
                          </Badge>
                        </HStack>
                        <HStack spacing={2} align="center">
                          <Text fontSize="sm" color="gray.600">
                            {row.samplingPoint.country ?? '-'}
                          </Text>
                          <Badge colorScheme={envTypeToColorScheme(row.samplingPoint.type)}>
                            {friendlySampleType(row.samplingPoint.type as SampleType)}
                          </Badge>
                        </HStack>
                        <HStack spacing={2} mt={1} wrap="wrap">
                          {row.projects.map(p => (
                            <Badge as={Link} to={`/research/${p.id}`} key={p.id} colorScheme="green">
                              {p.name}
                            </Badge>
                          ))}
                        </HStack>
                      </Box>
                      <HStack spacing={3}>
                        <ChipsPopover sampleId={row.sampleId} chipsCount={row.chipsCount} />
                        <AccessPopover sampleId={row.sampleId} />
                      </HStack>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
        <Flex justify="space-between" align="center" mt={2}>
          <Text fontSize="sm">Total: {data.total}</Text>
          <HStack>
            <ResistomapSelect<Option, false, GroupBase<Option>>
              options={pageSizeOptions}
              value={pageSize}
              onChange={opt => {
                if (opt) setPageSize(opt);
              }}
              placeholder="Page size"
              chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '160px' }) }}
            />
            <Button
              size="sm"
              onClick={() => {
                setPage(p => Math.max(1, p - 1));
              }}
              isDisabled={page <= 1}
            >
              Prev
            </Button>
            <Text>Page {page}</Text>
            <Button
              size="sm"
              onClick={() => {
                setPage(p => p + 1);
              }}
              isDisabled={data.rows.length < Number(pageSize.value)}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </div>
  );
}

function ChipsPopover({ sampleId, chipsCount }: { sampleId: number; chipsCount: number }) {
  const { data } = useAdminSampleChips(sampleId);
  // If exactly one chip, make the badge a direct link to the chip view
  if (chipsCount === 1 && data && data.length === 1) {
    const ch = data[0];
    return (
      <Badge as={Link} to={`/chip/${ch.id}`} target="_blank" rel="noreferrer" colorScheme="blue" cursor="pointer">
        Chips: 1
      </Badge>
    );
  }

  // Otherwise, keep tooltip showing the list of chips on hover
  return (
    <Tooltip
      label={
        <VStack align="stretch">
          <Text fontWeight="bold">Chips ({chipsCount})</Text>
          {(data || []).map(ch => (
            <Link key={ch.id} to={`/chip/${ch.id}`} target="_blank" rel="noreferrer">
              {ch.name} {ch.runDate ? `(${new Date(ch.runDate).toISOString().slice(0, 10)})` : ''}
            </Link>
          ))}
        </VStack>
      }
      hasArrow
      openDelay={300}
    >
      <Badge colorScheme="blue" cursor="pointer">
        Chips: {chipsCount}
      </Badge>
    </Tooltip>
  );
}

function AccessPopover({ sampleId }: { sampleId: number }) {
  const { data } = useAdminSampleAccess(sampleId);
  const orgCount = data?.organisations.length || 0;
  const userCount = data?.users.length || 0;
  return (
    <Tooltip
      label={
        <VStack align="stretch" maxW="320px">
          <Text fontWeight="bold">Access</Text>
          <Text>Organisations ({orgCount})</Text>
          <VStack align="stretch" spacing={1}>
            {(data?.organisations || []).map(o => (
              <Text key={o.id}>
                {o.name} {o.accessLevel === AccessLevel.OWNER ? '(O)' : '(R)'}
              </Text>
            ))}
          </VStack>
          <Text mt={2}>Users ({userCount})</Text>
          <VStack align="stretch" spacing={1}>
            {(data?.users || []).map(u => (
              <Text key={u.id}>
                {u.firstName} {u.lastName} {u.email ? `(${u.email})` : ''}{' '}
                {u.accessLevel === AccessLevel.OWNER ? '(O)' : '(R)'}
              </Text>
            ))}
          </VStack>
        </VStack>
      }
      hasArrow
      openDelay={300}
    >
      <Badge colorScheme="purple" cursor="pointer">
        Access: {userCount}/{orgCount}
      </Badge>
    </Tooltip>
  );
}
